import {AUTO_MODEL_ID} from '@github-ui/copilot-chat-helpers/auto-model'
import {isCustomModel} from '@github-ui/copilot-chat-state/models'
import {type CopilotChatModel, CopilotPlan, type RequiredPlan} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'

type ModelPromo = NonNullable<NonNullable<CopilotChatModel['billing']>['promo']>

export interface ModelLockContext {
  plan: CopilotPlan | null
  chatQuotaExceeded: boolean
  premiumInteractionsQuotaExceeded: boolean
  overagesEnabled: boolean
}

export interface ModelLockState {
  freePlanAndOutOfQuota: boolean
  premiumModelAndOutOfQuota: boolean
  modelOutOfPlan: boolean
  isUpsellModel: boolean
  locked: boolean
}

/**
 * Represents a group of custom models with a display name and owner type
 */
export interface CustomModelGroup {
  displayName: string
  ownerType?: 'organization' | 'enterprise'
  models: CopilotChatModel[]
}

/**
 * Model tier configuration
 */
export interface ModelTierConfig {
  lowTier: CopilotChatModel[]
  midTier: CopilotChatModel[]
  highTier: CopilotChatModel[]
  base: CopilotChatModel[]
  preview: CopilotChatModel[]
  custom: {[groupKey: string]: CustomModelGroup}
}

// individual-plan tiers ordered from least to most privileged. Used to infer access to a CAPI-returned model
const INDIVIDUAL_PLAN_TIERS_ASCENDING: readonly CopilotPlan[] = [
  CopilotPlan.IndividualStudent,
  CopilotPlan.IndividualPro,
  CopilotPlan.IndividualProPlus,
  CopilotPlan.IndividualMax,
]

// `business` is intentionally omitted: there's no individual self-serve upgrade path to Business,
const MIN_PLAN_FOR_REQUIRED_PLAN: Readonly<Partial<Record<RequiredPlan, CopilotPlan>>> = {
  edu: CopilotPlan.IndividualStudent,
  pro: CopilotPlan.IndividualPro,

  pro_plus: CopilotPlan.IndividualProPlus,
  max: CopilotPlan.IndividualMax,
}

/**
 * Returns the minimum individual plan that can access `model`, or undefined if the
 * model is unrestricted or only available to org plans (Business/Enterprise). For
 * backend-curated models with `requiredPlan` set this uses the static map; otherwise
 * it's the lowest-tier individual plan listed in `restricted_to`.
 */
export function getMinimumRequiredPlan(model: CopilotChatModel): CopilotPlan | undefined {
  if (model.requiredPlan) {
    return MIN_PLAN_FOR_REQUIRED_PLAN[model.requiredPlan]
  }
  const restricted = model.billing?.restricted_to
  if (!restricted?.length) return undefined
  return INDIVIDUAL_PLAN_TIERS_ASCENDING.find(plan => restricted.includes(plan))
}

// Returns the sole "usable" model when exactly one exists, otherwise null. A model
// is usable when it's picker-enabled and not a disabled upsell row, so the result is
// non-null only when the user is effectively locked to a single model (e.g. Free/EDU
// on Auto, with the remaining rows surviving as upsell prompts).
export function getOnlyEnabledModel(models: CopilotChatModel[]): CopilotChatModel | null {
  const enabledModels = models.filter(model => model.model_picker_enabled && model.is_upsell !== true)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return enabledModels.length === 1 ? enabledModels[0]! : null
}

/**
 * Low tier model exceptions - models that should be in low tier despite billing multiplier
 */
const LOW_TIER_MODEL_EXCEPTIONS = ['o3-mini', 'Gemini 2.0 Flash']

/**
 * Determines if a model belongs to the low tier based on category or billing
 */
function isLowTierModel(model: CopilotChatModel): boolean {
  // If category field is present, use it
  if (model.model_picker_category !== undefined) {
    return model.model_picker_category === 'lightweight'
  }

  // Fallback to billing multiplier logic
  return (
    (model.billing &&
      ((model.billing?.multiplier < 1 && model.billing?.multiplier !== 0) ||
        LOW_TIER_MODEL_EXCEPTIONS.includes(model.displayName))) ||
    false
  )
}

/**
 * Determines if a model belongs to the mid tier based on category or billing
 */
function isMidTierModel(model: CopilotChatModel): boolean {
  // If category field is present, use it
  if (model.model_picker_category !== undefined) {
    return model.model_picker_category === 'versatile'
  }

  // Fallback to billing multiplier logic
  return (
    (model.billing &&
      (model.billing?.multiplier === 1 || model.billing?.multiplier === 0) &&
      !LOW_TIER_MODEL_EXCEPTIONS.includes(model.displayName)) ||
    false
  )
}

/**
 * Determines if a model belongs to the high tier based on category or billing
 */
function isHighTierModel(model: CopilotChatModel): boolean {
  // If category field is present, use it
  if (model.model_picker_category !== undefined) {
    return model.model_picker_category === 'powerful'
  }

  // Fallback to billing multiplier logic
  return (model.billing && model.billing?.multiplier > 1) || false
}

/**
 * Checks if models have diversity in their categories (either by category field or billing).
 * Used by `ModelPickerDesign` to decide whether the tier-grouped layout is meaningful
 * for the current model list. When the backend returns a fully-categorized response
 * (the production case) this is always true; the flat-mode fallback handles legacy or
 * synthetic data that has no `model_picker_category` to bucket on.
 */
export function hasModelCategoryDiversity(models: CopilotChatModel[]): boolean {
  const categoryDiversity =
    models.some(m => m.model_picker_category !== undefined) &&
    new Set(models.filter(m => m.model_picker_category !== undefined).map(m => m.model_picker_category)).size > 1

  const billingDiversity =
    models.some(m => m.billing?.multiplier !== undefined) &&
    new Set(models.filter(m => m.billing?.multiplier !== undefined).map(m => m.billing?.multiplier)).size > 1

  return categoryDiversity || billingDiversity
}

/**
 * Creates a unique group key for custom models based on owner name and type.
 * This ensures enterprise and org models with the same name are in separate groups.
 */
function getCustomModelGroupKey(model: CopilotChatModel): string {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const ownerName = model.custom_model!.owner_name
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const ownerType = model.custom_model!.owner_type ?? 'organization'
  return `${ownerName}::${ownerType}`
}

export interface CategorizeModelsOptions {
  shouldShowCategories: boolean
  shouldShowPreviewModelsSeparately?: boolean
  modelLockContext?: ModelLockContext
}

/**
 * Adds "(enterprise)" or "(organization)" suffix only when there are two with the same name.
 */
function getDisplayName(
  ownerName: string,
  ownerType: 'organization' | 'enterprise' | undefined,
  allGroups: {[groupKey: string]: {ownerName: string; ownerType?: 'organization' | 'enterprise'}},
): string {
  // Check if there's another group with the same owner name but different owner type
  const hasNameCollision = Object.values(allGroups).some(
    group =>
      group.ownerType !== ownerType &&
      group.ownerName.trim().toLocaleLowerCase() === ownerName.trim().toLocaleLowerCase(),
  )

  if (hasNameCollision && ownerType) {
    return `${ownerName} (${ownerType})`
  }
  return ownerName
}

/**
 * Sorts models alphabetically in descending order by display name.
 * This ensures newer models (e.g. "Claude Opus 4.8") appear above older ones (e.g. "Claude Opus 4.7").
 */
function sortModelsDescending(models: CopilotChatModel[]): CopilotChatModel[] {
  return [...models].sort((a, b) => b.displayName.localeCompare(a.displayName))
}

const UTC_TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?Z$/

export function getModelPromoExpiry(endsAt: string | undefined): number | undefined {
  if (!endsAt) return undefined
  const match = UTC_TIMESTAMP_PATTERN.exec(endsAt)
  if (!match) return undefined

  const expiry = Date.parse(endsAt)
  if (!Number.isFinite(expiry)) return undefined

  const parsed = new Date(expiry)
  const [, year, month, day, hour, minute, second] = match
  const matchesParsedDate =
    parsed.getUTCFullYear() === Number(year) &&
    parsed.getUTCMonth() + 1 === Number(month) &&
    parsed.getUTCDate() === Number(day) &&
    parsed.getUTCHours() === Number(hour) &&
    parsed.getUTCMinutes() === Number(minute) &&
    parsed.getUTCSeconds() === Number(second)

  return matchesParsedDate ? expiry : undefined
}

export function isModelPromoActive(promo: ModelPromo | undefined, now = Date.now()): boolean {
  if (!promo) return false
  if (promo.ends_at === undefined) return true

  const expiry = getModelPromoExpiry(promo.ends_at)
  return expiry !== undefined && expiry > now
}

export function getModelLockState(model: CopilotChatModel, context: ModelLockContext): ModelLockState {
  const freePlanAndOutOfQuota = context.plan === CopilotPlan.IndividualFree && context.chatQuotaExceeded
  const premiumModelAndOutOfQuota =
    !!model.billing?.is_premium &&
    context.plan !== CopilotPlan.IndividualFree &&
    context.premiumInteractionsQuotaExceeded &&
    !context.overagesEnabled
  const modelOutOfPlan =
    context.plan !== null &&
    model.billing?.restricted_to !== undefined &&
    !model.billing.restricted_to.includes(context.plan)
  const isUpsellModel = model.is_upsell === true

  return {
    freePlanAndOutOfQuota,
    premiumModelAndOutOfQuota,
    modelOutOfPlan,
    isUpsellModel,
    locked: freePlanAndOutOfQuota || premiumModelAndOutOfQuota || modelOutOfPlan || isUpsellModel,
  }
}

function partitionPromotedModels(
  models: CopilotChatModel[],
  modelLockContext: ModelLockContext | undefined,
): [promotedModels: CopilotChatModel[], remainingModels: CopilotChatModel[]] {
  if (!copilotFeatureFlags.modelPickerPromotions) return [[], models]

  return models.reduce<[CopilotChatModel[], CopilotChatModel[]]>(
    (groups, model) => {
      const isLocked = modelLockContext ? getModelLockState(model, modelLockContext).locked : false
      groups[model.model_picker_enabled && !isLocked && isModelPromoActive(model.billing?.promo) ? 0 : 1].push(model)
      return groups
    },
    [[], []],
  )
}

/**
 * Categorizes models into tiers for the model picker
 */
export function categorizeModels(models: CopilotChatModel[], options: CategorizeModelsOptions): ModelTierConfig {
  const {shouldShowCategories, shouldShowPreviewModelsSeparately = true, modelLockContext} = options

  const [customModels, defaultModels] = models.reduce<[CopilotChatModel[], CopilotChatModel[]]>(
    (acc, m) => {
      acc[isCustomModel(m) ? 0 : 1].push(m)
      return acc
    },
    [[], []],
  )
  const [promotedModels, remainingDefaultModels] = partitionPromotedModels(defaultModels, modelLockContext)

  const groupInfo: {[groupKey: string]: {ownerName: string; ownerType?: 'organization' | 'enterprise'}} = {}
  for (const m of customModels) {
    const groupKey = getCustomModelGroupKey(m)
    if (!groupInfo[groupKey]) {
      groupInfo[groupKey] = {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ownerName: m.custom_model!.owner_name,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ownerType: m.custom_model!.owner_type,
      }
    }
  }

  const customModelGroups = customModels.reduce(
    (acc, m) => {
      const groupKey = getCustomModelGroupKey(m)
      if (!acc[groupKey]) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const ownerName = m.custom_model!.owner_name
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const ownerType = m.custom_model!.owner_type
        acc[groupKey] = {
          displayName: getDisplayName(ownerName, ownerType, groupInfo),
          ownerType,
          models: [],
        }
      }
      acc[groupKey].models.push(m)
      return acc
    },
    {} as {[groupKey: string]: CustomModelGroup},
  )

  if (!shouldShowCategories) {
    return {
      lowTier: [],
      midTier: [],
      highTier: [],
      base: [
        ...promotedModels,
        ...(shouldShowPreviewModelsSeparately
          ? remainingDefaultModels.filter(model => !model.preview)
          : remainingDefaultModels),
      ],
      preview: shouldShowPreviewModelsSeparately ? remainingDefaultModels.filter(model => model.preview) : [],
      custom: customModelGroups,
    }
  }

  for (const group of Object.values(customModelGroups)) {
    group.models = sortModelsDescending(group.models)
  }

  // If Auto Model exists (CCA), show it in the `base` section instead of categorizing it.
  const isAutoModel = (m: CopilotChatModel): boolean => m.id === AUTO_MODEL_ID
  const baseModels = [...promotedModels, ...remainingDefaultModels.filter(model => isAutoModel(model))]
  const categorizableModels = remainingDefaultModels.filter(model => !isAutoModel(model))

  return {
    lowTier: sortModelsDescending(categorizableModels.filter(m => isLowTierModel(m))),
    midTier: sortModelsDescending(categorizableModels.filter(m => isMidTierModel(m))),
    highTier: sortModelsDescending(categorizableModels.filter(m => isHighTierModel(m))),
    base: baseModels,
    preview: [],
    custom: customModelGroups,
  }
}

/** Two model records describe the same model when they share an id. */
export function isSameModel(oldModel: CopilotChatModel, newModel: CopilotChatModel): boolean {
  return oldModel.id === newModel.id
}
